// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyPicture from "@synd/MobiusOverrides/LazyImage/LazyPicture";
import { xParserOptions } from "@synd/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";

const enum fields {
    background = "background",
    image = "image",
    backgroundColor = "backgroundColor",
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    hideButton = "hideButton",
    customCSS = "customCSS",
    imagePosition = "imagePosition",
    imageAltText = "imageAltText",
    openInNewWindow = "openInNewWindow",
    desktopImage = "desktopImage",
    tabletImage = "tabletImage",
    mobileImage = "mobileImage",
    addBorder = "addBorder",
    isHorizontalImage = "isHorizontalImage",
    numberOfSubheadingLines = "numberOfSubheadingLines",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary" | "link";
        [fields.hideButton]: boolean;
        [fields.customCSS]: string;
        [fields.imagePosition]: "right" | "left";
        [fields.imageAltText]: string;
        [fields.openInNewWindow]: boolean;
        [fields.desktopImage]: string;
        [fields.tabletImage]: string;
        [fields.mobileImage]: string;
        [fields.addBorder]: boolean;
        [fields.isHorizontalImage]: boolean;
        [fields.numberOfSubheadingLines]: number;
    };
}

export interface InfoCardStyles {
    wrapper?: InjectableCss;
    infoCardButton?: ButtonPresentationProps;
    heading?: TypographyProps;
    subHeading?: TypographyProps;
    imageWrapperStyles?: InjectableCss;
    textWrapperStyles?: InjectableCss;
    infoCardButtonWrapper?: InjectableCss;
    content?: InjectableCss;
}

export const infoCardStyles: InfoCardStyles = {};

export const InfoCard: FC<OwnProps> = ({
    fields: {
        backgroundColor,
        buttonLabel,
        buttonLink,
        heading,
        hideButton,
        desktopImage,
        mobileImage,
        tabletImage,
        imageAltText,
        openInNewWindow,
        subheading,
        variant,
        addBorder,
        isHorizontalImage,
        numberOfSubheadingLines,
    },
}) => {
    const { url, title } = useGetLink(buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            openInNewWindow ? window.open(url) : (window.location.href = url);
        } else {
            openInNewWindow ? window.open(url) : history.push(url);
        }
    };

    const styles = infoCardStyles;

    return (
        <StyledWrapper {...styles.wrapper} {...{ backgroundColor, addBorder }}>
            <StyledWrapper {...styles.imageWrapperStyles} {...{ isHorizontalImage }}>
                <LazyPicture
                    smallImageSrc={mobileImage || tabletImage}
                    mediumImageSrc={tabletImage}
                    largeImageSrc={desktopImage || tabletImage}
                    altText={imageAltText}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.content}>
                <StyledWrapper {...styles.textWrapperStyles}>
                    {heading && <Typography {...styles.heading}>{parse(heading, xParserOptions)}</Typography>}

                    {subheading && <Typography {...styles.subHeading} {...{numberOfSubheadingLines}}>{parse(subheading, xParserOptions)}</Typography>}
                </StyledWrapper>
                <StyledWrapper {...styles.infoCardButtonWrapper}>
                    {!hideButton && (buttonLabel || title || url) && (
                        <Button
                            {...styles.infoCardButton}
                            variant={variant as any}
                            onClick={onClick}
                            aria-label={buttonLabel || title || url}
                        >
                            {buttonLabel || title || url}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const infoCard: WidgetModule = {
    component: InfoCard,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.desktopImage,
                displayName: "Large Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.tabletImage,
                displayName: "Medium Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.mobileImage,
                displayName: "Small Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.imageAltText,
                editorTemplate: "TextField",
                displayName: "Image Alt Text",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.isHorizontalImage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                displayName: "Is Horizontal Image",
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "#F9F8F6",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Title",
                defaultValue: "Title",
                extendedConfig: { height: 100 },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subtitle",
                defaultValue: "Subtitle",
                extendedConfig: { height: 170 },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.numberOfSubheadingLines,
                displayName: "Number of Subtitle Lines",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "CTA Button Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                    { displayName: "Link", value: "link" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "CTA Button Label",
                defaultValue: "Label",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "CTA Button URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.hideButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.addBorder,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
        ],
    },
};

export default infoCard;
